import React, { useEffect, useMemo, useState } from 'react'
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import toast from 'react-hot-toast';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import ActionDropdown from '../../components/common/ActionDropdown';
import { useAuth } from '../../contexts/AuthContext'
import { useDepartments, useDeleteDepartment } from '../../hooks/useDepartments'

export default function Department() {

    const [addDepartmentModal, setAddDepartmentModal] = useState(false)
    const openAddDepartmentModal = () => {
        setAddDepartmentModal(!addDepartmentModal)
    }
    useEffect(() => {
        document.body.classList[addDepartmentModal ? "add" : "remove"]("overflow-hidden")
    }, [addDepartmentModal])

    const { user } = useAuth()
    const orgId = user?.orgId
    const { data: departments = [], isLoading } = useDepartments(orgId)
    const deleteDepartmentMutation = useDeleteDepartment()

    const [filterText, setFilterText] = useState('')
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    // Action dropdown state is self-contained

    const handleDelete = (row) => {
        setItemToDelete(row);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteDepartmentMutation.mutateAsync(itemToDelete.id);
            toast.success(`Department "${itemToDelete.name}" deleted successfully`);
            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting department:', error);
        }
    };

    const filteredDepartments = departments.filter(dept =>
        dept.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        dept.code?.toLowerCase().includes(filterText.toLowerCase())
    )

    const columns = [
        {
            name: '#',
            selector: (row, index) => index + 1,
            sortable: true,
            width: '80px',
        },
        {
            name: 'Department Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Code',
            selector: row => row.code,
            sortable: true,
        },
        // Assuming backend returns head info or we need another way to show it. 
        // For now, removing 'Department Head' if not in simple dept object, or just showing generic if available.
        // If the backend has a 'head' field:
        // {
        //     name: 'Department Head',
        //     selector: row => row.dept_head_name || 'N/A',
        //     sortable: true,
        // },
        {
            name: 'Total Employee',
            selector: row => row.employee_count || 0, // Backend should ideally provide this count
            sortable: true,
        },
        {
            name: 'Action',
            cell: row => (
                <div className="flex justify-center">
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'Edit Department',
                                icon: IconEdit,
                                onClick: () => toast('Edit department feature coming soon'),
                                description: "Update department name and settings"
                            },
                            {
                                label: 'Delete Department',
                                icon: IconTrash,
                                danger: true,
                                onClick: () => handleDelete(row),
                                description: "Permanently remove this department"
                            }
                        ]}
                    />
                </div>
            ),
        },
    ];

    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
            }
        };

        return (
            <div className='flex gap-5 mb-10 w-full justify-end'>
                <div className='relative w-full max-w-[221px]'>
                    <input
                        type='text'
                        id='search'
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        placeholder='Search here...'
                        className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                    />
                </div>
                <button onClick={handleClear} className="btn btn-danger">
                    Clear
                </button>
            </div>
        );
    }, [filterText, resetPaginationToggle]);

    if (isLoading) return <div className="p-20">Loading...</div>

    return (
        <>
            <Breadcrumb title="Departments" path="Employees / Departments" />
            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                <div className='flex items-center justify-between gap-15 mb-30'>
                    <p className='text-[16px]/[19.2px] font-medium'>
                        Department List
                    </p>
                    <button onClick={openAddDepartmentModal} className='btn btn-primary'>
                        Add New
                    </button>
                </div>
                <div className='react-data-table'>
                    <ReactDataTable
                        columns={columns}
                        data={filteredDepartments}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponentMemo}
                    />
                </div>
            </div>
            {addDepartmentModal &&
                <>
                    <div className={`fixed p-15 w-full max-w-[600px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]`}>
                        <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                            <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                                Add Department
                            </h5>
                            <div className='my-10 md:px-10 px-[7px] max-h-[60vh] overflow-auto cus-scrollbar'>
                                <div className='grid grid-cols-12 gap-20'>
                                    <div className='form-group flex col-span-12'>
                                        <input type='text' id='departmentName' name='departmentName' placeholder='Department Name' />
                                    </div>
                                    <div className='form-group flex md:col-span-6 col-span-12'>
                                        <input type='text' id='departmentHead' name='departmentHead' placeholder='Department Head' />
                                    </div>
                                    <div className='form-group flex md:col-span-6 col-span-12'>
                                        <input type='text' id='totalEmployee' name='totalEmployee' placeholder='Total Employees' />
                                    </div>
                                </div>
                            </div>
                            <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                                <button onClick={openAddDepartmentModal} className='btn btn-outline-secondary'>
                                    Cancel
                                </button>
                                <button className='btn btn-primary'>
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={openAddDepartmentModal} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
                </>
            }
            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                title="Delete Department"
                message="Are you sure you want to delete this department? All associated data will be removed."
                itemName={itemToDelete?.name}
                isLoading={deleteDepartmentMutation.isPending}
            />

        </>
    )
}
